import { useJsApiLoader } from "@react-google-maps/api";
import React, { useState } from "react";

import { LocatorMapBlock as Value } from "@reactivated";

import { useNearbyRetailLocations, useUserLocation } from "../../api/locations";
import { ReactivatedContext as Context } from "../../context";
import { LocationID, MILES_TO_METERS, Meters, isoMeters } from "../../models";
import { ButtonPrimary } from "../Clickables";
import { LocatorMap } from "../LocatorMap";
import { LocatorMap as LocatorMap2024 } from "../LocatorMap2024";
import { MarkerMap as MarkerMap2024 } from "../LocatorMap2024/MarkerMap";
import { GOOGLE_LIBRARIES, MarkerMap } from "../LocatorMap/MarkerMap";

import styles from "./index.module.scss";

const DEFAULT_RADIUS: Meters = isoMeters.wrap(100 * MILES_TO_METERS);

export const LocatorMapBlock = (props: { value: Value }) => {
    const context = React.useContext(Context);
    const apiKey = context.GOOGLE_MAPS_V3_APIKEY || "";
    const mapType = props.value.map_type || "DEALER";
    const isV2026 = context.DESIGN_VERSION === "v2026";

    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: apiKey,
        libraries: GOOGLE_LIBRARIES,
    });
    const { userLocation, setEnteredLocation } = useUserLocation(mapType);
    const [focusedLocationID, setFocusedLocationID] =
        useState<LocationID | null>(null);
    const [error, setError] = useState("");
    const [radius, setRadius] = useState<Meters>(DEFAULT_RADIUS);
    const queryResp = useNearbyRetailLocations(
        userLocation,
        mapType,
        isV2026 ? radius : undefined,
    );
    const locations = queryResp?.locations || [];
    const effectiveFocusedID =
        focusedLocationID ?? locations[0]?.location.id ?? null;

    if (!isLoaded) return null;

    const mapSlot = isV2026 ? (
        <MarkerMap
            locations={locations}
            focusedLocationID={effectiveFocusedID}
            setFocusedLocationID={setFocusedLocationID}
            center={userLocation.entered || userLocation.detected}
            bounds={userLocation.bounds}
            onSearchArea={setEnteredLocation}
        />
    ) : (
        <MarkerMap2024
            locations={locations}
            focusedLocationID={focusedLocationID}
            setFocusedLocationID={setFocusedLocationID}
            bounds={userLocation.bounds}
        />
    );

    if (isV2026) {
        return (
            <LocatorMap
                locations={locations}
                focusedLocationID={effectiveFocusedID}
                setFocusedLocationID={setFocusedLocationID}
                error={error}
                onLocationSearch={setEnteredLocation}
                onError={setError}
                radius={radius}
                onRadiusChange={setRadius}
                mapSlot={mapSlot}
            />
        );
    }

    const extraLinks = props.value.links && (
        <div className={styles.extraLinks}>
            {props.value.links.map((link, i) => (
                <ButtonPrimary key={i} href={link.url!}>
                    {link.display_name}
                </ButtonPrimary>
            ))}
        </div>
    );

    return (
        <LocatorMap2024
            locations={locations}
            focusedLocationID={focusedLocationID}
            setFocusedLocationID={setFocusedLocationID}
            error={error}
            onLocationSearch={setEnteredLocation}
            onError={setError}
            mapType={mapType}
            mapSlot={mapSlot}
        >
            {extraLinks}
        </LocatorMap2024>
    );
};
